import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../AuthContext';
import { Plus, Trash2, Edit2, X, CheckCircle, XCircle, Users } from 'lucide-react';
import api from '../../api';

const PERIODS = [
  { key: 'daily', label: 'Hoy' },
  { key: 'weekly', label: 'Semana' },
  { key: 'monthly', label: 'Mes' },
  { key: 'yearly', label: 'Año' },
  { key: 'custom', label: 'Rango' },
];

const fmt = (n) => `$${(n || 0).toLocaleString('es-AR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

export default function UsersPanel() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('monthly');
  const [revenues, setRevenues] = useState(null);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingStaff, setEditingStaff] = useState(null);
  const [formUsername, setFormUsername] = useState('');
  const [formName, setFormName] = useState('');
  const [usernameStatus, setUsernameStatus] = useState(null); // null | 'checking' | 'available' | 'taken'
  const [saving, setSaving] = useState(false);

  const loadStaff = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true);
    try {
      const r = await api.get(`/business/owner/${user.id}/staff`);
      setStaff(r.data || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [user?.id]);

  const loadRevenues = useCallback(async () => {
    if (!user?.id) return;
    if (period === 'custom' && (!fromDate || !toDate)) return;
    try {
      const params = { period };
      if (period === 'custom' && fromDate && toDate) {
        params.from_date = fromDate;
        params.to_date = toDate;
      }
      const r = await api.get(`/analytics/owner/${user.id}`, { params });
      setRevenues(r.data);
    } catch (e) { console.error(e); }
  }, [user?.id, period, fromDate, toDate]);

  useEffect(() => { loadStaff(); }, [loadStaff]);
  useEffect(() => { loadRevenues(); }, [loadRevenues]);

  // Debounced username check
  useEffect(() => {
    if (!showModal || !formUsername.trim() || (editingStaff && editingStaff.username === formUsername)) {
      setUsernameStatus(null);
      return;
    }
    setUsernameStatus('checking');
    const t = setTimeout(async () => {
      try {
        const r = await api.get(`/business/check-username?username=${encodeURIComponent(formUsername.trim())}`);
        setUsernameStatus(r.data.available ? 'available' : 'taken');
      } catch { setUsernameStatus(null); }
    }, 500);
    return () => clearTimeout(t);
  }, [formUsername, showModal, editingStaff]);

  const openCreate = () => { setEditingStaff(null); setFormUsername(''); setFormName(''); setUsernameStatus(null); setShowModal(true); };
  const openEdit = (s) => { setEditingStaff(s); setFormUsername(s.username); setFormName(s.employee_name); setUsernameStatus(null); setShowModal(true); };

  const handleSave = async () => {
    if (!formUsername.trim() || !formName.trim()) { alert('Completa todos los campos'); return; }
    if (usernameStatus === 'taken') { alert('El nombre de usuario ya está en uso, coloca otro'); return; }
    if (!editingStaff && usernameStatus !== 'available') { alert('Verificá que el usuario esté disponible'); return; }
    setSaving(true);
    try {
      if (editingStaff) {
        await api.put(`/business/staff/${editingStaff.id}`, { username: formUsername.trim(), employee_name: formName.trim() });
        alert('Empleado actualizado');
      } else {
        await api.post(`/business/owner/${user.id}/staff`, { username: formUsername.trim(), employee_name: formName.trim() });
        alert('Empleado creado exitosamente');
      }
      setShowModal(false);
      loadStaff();
      loadRevenues();
    } catch (e) {
      alert(e.response?.data?.detail || 'Error al guardar');
    } finally { setSaving(false); }
  };

  const handleDelete = async (s) => {
    if (!window.confirm(`¿Eliminar a ${s.employee_name}?`)) return;
    try {
      await api.delete(`/business/staff/${s.id}`);
      loadStaff();
      loadRevenues();
    } catch (e) { alert(e.response?.data?.detail || 'Error'); }
  };

  const getStaffRevenue = (staffId) => {
    if (!revenues?.staff_revenues) return null;
    return revenues.staff_revenues.find(r => r.staff_id === staffId);
  };

  return (
    <div className="page-full">
      <div className="header">
        <button className="icon-btn" onClick={() => navigate(-1)}>←</button>
        <h1 className="header-title" style={{ flex: 1 }}>Mis Usuarios</h1>
        <button className="icon-btn" data-testid="add-staff-btn" onClick={openCreate}><Plus size={24} /></button>
      </div>

      {/* Revenue Period Tabs */}
      <div className="content-scroll">
        <div className="period-tabs">
          {PERIODS.map(p => (
            <button key={p.key} className={`period-tab ${period === p.key ? 'active' : ''}`}
              onClick={() => setPeriod(p.key)}>{p.label}</button>
          ))}
        </div>

        {period === 'custom' && (
          <div className="date-range-row">
            <div className="form-group" style={{ flex: 1 }}>
              <label className="label">Desde</label>
              <input className="input" type="date" data-testid="owner-range-from" value={fromDate} onChange={e => setFromDate(e.target.value)} />
            </div>
            <div className="form-group" style={{ flex: 1 }}>
              <label className="label">Hasta</label>
              <input className="input" type="date" data-testid="owner-range-to" value={toDate} onChange={e => setToDate(e.target.value)} />
            </div>
          </div>
        )}

        {/* Owner Revenue Summary */}
        {revenues && (
          <div className="card" style={{ marginBottom: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <Users size={18} color="var(--accent)" />
              <span style={{ fontWeight: 700 }}>Mis Ganancias</span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, textAlign: 'center' }}>
              <div>
                <div className="stat-label">Mis Ventas</div>
                <div className="stat-value" style={{ fontSize: '1rem', color: 'var(--accent)' }}>{fmt(revenues.owner_revenue?.total_revenue)}</div>
              </div>
              <div>
                <div className="stat-label">Ganancias Staff</div>
                <div className="stat-value" style={{ fontSize: '1rem', color: '#4CAF50' }}>{fmt(revenues.staff_revenues?.reduce((s, r) => s + r.total_revenue, 0))}</div>
              </div>
              <div>
                <div className="stat-label">Total</div>
                <div className="stat-value" style={{ fontSize: '1rem', fontWeight: 700 }}>{fmt(revenues.total_combined)}</div>
              </div>
            </div>
          </div>
        )}

        {/* Staff List */}
        <h3 style={{ marginBottom: '12px', fontWeight: 600 }}>Empleados / Socios</h3>
        {loading ? <div className="spinner" /> : staff.length === 0 ? (
          <div className="empty-state">
            <p>No tenés empleados o socios cargados.</p>
            <p>Tocá + para agregar.</p>
          </div>
        ) : (
          staff.map(s => {
            const rev = getStaffRevenue(s.id);
            return (
              <div key={s.id} className="card" data-testid={`staff-card-${s.id}`} style={{ marginBottom: '12px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{
                    width: 40, height: 40, borderRadius: '50%', background: 'var(--accent)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#000', fontWeight: 700, fontSize: 16
                  }}>{s.employee_name.charAt(0).toUpperCase()}</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 700 }}>{s.employee_name}</div>
                    <div style={{ fontSize: '0.8rem', color: 'var(--text2)' }}>@{s.username}</div>
                  </div>
                  <button className="icon-btn" onClick={() => openEdit(s)}><Edit2 size={16} /></button>
                  <button className="icon-btn" style={{ color: 'var(--danger)' }} onClick={() => handleDelete(s)}><Trash2 size={16} /></button>
                </div>
                {rev && (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 10, paddingTop: 10, borderTop: '1px solid var(--border)' }}>
                    <div style={{ textAlign: 'center' }}>
                      <div className="stat-label">Ingresos</div>
                      <div style={{ fontWeight: 700, color: 'var(--accent)' }}>{fmt(rev.total_revenue)}</div>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <div className="stat-label">Ventas</div>
                      <div style={{ fontWeight: 700 }}>{rev.total_sales}</div>
                    </div>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content modal-center" onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}>
              <h3 className="modal-title" style={{ flex: 1, margin: 0 }}>{editingStaff ? 'Editar Empleado' : 'Nuevo Empleado / Socio'}</h3>
              <button className="icon-btn" onClick={() => setShowModal(false)}><X size={20} /></button>
            </div>

            <div className="form-group">
              <label className="label">Nombre del Empleado / Socio</label>
              <input className="input" data-testid="staff-name-input" placeholder="Ej: Juan Pérez" value={formName} onChange={e => setFormName(e.target.value)} />
            </div>

            <div className="form-group">
              <label className="label">Usuario de Acceso</label>
              <div style={{ position: 'relative' }}>
                <input className="input" data-testid="staff-username-input" placeholder="Ej: Juan2026*" value={formUsername}
                  onChange={e => setFormUsername(e.target.value)}
                  style={{ paddingRight: '36px', borderColor: usernameStatus === 'taken' ? 'var(--danger)' : usernameStatus === 'available' ? '#4CAF50' : undefined }} />
                <div style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)' }}>
                  {usernameStatus === 'checking' && <div style={{ width: 16, height: 16, borderRadius: '50%', border: '2px solid var(--accent)', borderTopColor: 'transparent', animation: 'spin 0.8s linear infinite' }} />}
                  {usernameStatus === 'available' && <CheckCircle size={16} color="#4CAF50" />}
                  {usernameStatus === 'taken' && <XCircle size={16} color="var(--danger)" />}
                </div>
              </div>
              {usernameStatus === 'taken' && <p style={{ color: 'var(--danger)', fontSize: '0.8rem', marginTop: 4 }}>El nombre ya está en uso, coloca otro</p>}
              {usernameStatus === 'available' && <p style={{ color: '#4CAF50', fontSize: '0.8rem', marginTop: 4 }}>Usuario disponible</p>}
            </div>

            <div className="modal-buttons">
              <button className="btn-secondary" onClick={() => setShowModal(false)}>Cancelar</button>
              <button className="btn-primary" data-testid="save-staff-btn" onClick={handleSave} disabled={saving || usernameStatus === 'taken'}>{saving ? 'Guardando...' : 'Guardar'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
